/**
 * Campaigns helper functions
 */

import { CALL_ROUTER_BEHAVIOR_MODE_ALOAI, CALL_ROUTER_BEHAVIOR_MODE_DEAD_END, CALL_ROUTER_BEHAVIOR_MODE_IVR } from 'src/constants/campaign-call-router-behaviors'
import talk2TeamInboxApi from 'src/plugins/api/teamInboxApi'

/**
 * Generic function to fetch campaign data
 * @param {Object} context - Vue component context with access to Vuex actions
 * @param {Object} options - Options for the function
 * @param {Function} options.setAction - Vuex action to set the campaigns data
 * @param {Function} options.setLoadingAction - Vuex action to set the loading state
 * @param {Boolean} options.loadingFlag - Reference to the loading flag in the component
 * @param {Object} options.params - Additional parameters for the API call
 * @returns {Promise} - Promise that resolves when campaigns are loaded
 */
export const getCampaignsData = function (context, options = {}) {
  const {
    setAction = 'setCampaigns',
    setLoadingAction = 'setCampaignsIsLoading',
    loadingFlag = 'loadingCampaigns',
    params = { is_lite: true }
  } = options

  // Fixing it in case the default value is overridden
  params.is_lite = true

  if (!context.hasPermissionTo('list campaign')) {
    return Promise.resolve()
  }

  if (context[loadingFlag]) {
    // Avoid duplicate requests
    return Promise.resolve()
  }

  // Set loading state
  if (typeof context[setLoadingAction] === 'function') {
    context[setLoadingAction](true)
  } else {
    setCampaignsIsLoading(context, true)
  }

  return context.$axios
    .get('/api/v1/campaign', {
      mode: 'no-cors',
      params
    })
    .then((res) => {
      if (typeof context[setAction] === 'function') {
        context[setAction](res.data)
      }

      return Promise.resolve()
    })
    .catch((err) => {
      console.log(err)
      return Promise.reject()
    })
    .finally(() => {
      // Reset loading state
      if (typeof context[setLoadingAction] === 'function') {
        context[setLoadingAction](false)
      } else {
        setCampaignsIsLoading(context, false)
      }
    })
}

/**
 * Fetches campaigns data
 * @param {Object} context - Vue component context with access to Vuex actions
 * @returns {Promise} - Promise that resolves when campaigns are loaded
 */
export const getCampaigns = function (context) {
  return getCampaignsData(context, {
    setAction: 'setCampaigns',
    setLoadingAction: 'setCampaignsIsLoading',
    loadingFlag: 'loadingCampaigns'
  })
}

/**
 * Fetches team inbox campaign data
 * @param {Object} context - Vue component context with access to Vuex actions
 * @returns {Promise} - Promise that resolves when team inbox campaigns are loaded
 */
export const getTeamInboxCampaigns = async function (context) {
  return new Promise((resolve, reject) => {
    if (!context.hasPermissionTo('list campaign')) {
      return resolve()
    }

    if (typeof context.setLoadingTeamInboxCampaigns === 'function') {
      context.setLoadingTeamInboxCampaigns(true)
    }

    // Use optimized lines endpoint for Team Inbox
    return talk2TeamInboxApi.inboxes.lines()
      .then((res) => {
        if (typeof context.setTeamInboxCampaigns === 'function') {
          context.setTeamInboxCampaigns(res.data)
        }

        if (typeof context.setLoadingTeamInboxCampaigns === 'function') {
          context.setLoadingTeamInboxCampaigns(false)
        }

        return resolve()
      })
      .catch((err) => {
        console.log(err)

        if (typeof context.setLoadingTeamInboxCampaigns === 'function') {
          context.setLoadingTeamInboxCampaigns(false)
        }

        return reject()
      })
  })
}

/**
 * Sets the loading state for campaigns
 * @param {Object} context - Vue component context with access to Vuex actions
 * @param {Boolean} value - Loading state value
 */
export const setCampaignsIsLoading = function (context, value) {
  if (typeof context.setCampaignsIsLoading === 'function') {
    context.setCampaignsIsLoading(value)
  }
}

/**
 * Callback function to check if a campaign is available for an agent
 *
 * IVR and Dead End lines no longer get blanket visibility. Their access is derived from the
 * line's fallback Inbox: the backend computes the has_*_access flags against that fallback ring
 * group (see Campaign::scopeAddLineManagementFields), so an agent only sees an IVR/Dead End line
 * when they have access to its fallback Inbox.
 *
 * @param {Object} campaign - Campaign object
 * @param {Number} userId - User ID
 * @returns {Boolean} - True if the campaign is available for the agent, false otherwise
 */
export function agentAvailableCampaignsCallback (campaign, userId) {
  if (!campaign || !userId) {
    return false
  }

  if (!campaign.active) {
    return false
  }

  return campaign.user_id === userId ||
    campaign.has_direct_ring_group_access ||
    campaign.has_team_membership_access ||
    campaign.has_direct_watching_access ||
    campaign.has_team_watching_access ||
    campaign.has_personal_inbox_watching_access ||
    campaign.has_personal_inbox_team_watching_access
}

/**
 * Checks if a line can be picked to send SMS from: it needs a number to send from and
 * must not be a fax line. A2P/messaging blocks are checked separately by isMessagingBlocked.
 *
 * @param {Object} campaign - Campaign object
 * @returns {Boolean} - True if the line has an SMS-usable number
 */
export function isTextableCampaign (campaign) {
  return Boolean(campaign?.incoming_number || campaign?.incoming_numbers?.length) &&
    !campaign.is_fax
}

/**
 * Checks if a campaign is an IVR or dead end campaign
 *
 * @param {Object} campaign - Campaign object
 * @returns {Boolean} - True if the campaign is an IVR or dead end campaign, false otherwise
 */
export function isIvrOrDeadEndCampaign (campaign) {
  return [CALL_ROUTER_BEHAVIOR_MODE_DEAD_END, CALL_ROUTER_BEHAVIOR_MODE_IVR]
    .includes(campaign?.call_router_behavior)
}

/**
 * Checks if a line is visible to every user of the account instead of being scoped to an Inbox.
 *
 * AloAi lines have no ring group of their own and no fallback Inbox column, so no Inbox owns
 * them; fax lines route outside the Inbox model entirely. They are an explicit exception to
 * Inbox-derived visibility: every user of the account can see and use them. Mirrors the
 * company-wide predicate of Campaign::scopeAddLineManagementFields on the backend
 * (COMPANY_WIDE_CALL_ROUTER_BEHAVIORS plus is_fax), which grants the same exception through
 * has_direct_ring_group_access. IVR and Dead End lines are excluded — they carry a fallback
 * Inbox pointer, so they surface only inside that Inbox (see isIvrOrDeadEndCampaignInInbox).
 *
 * @param {Object} campaign - Campaign object
 * @returns {Boolean} - True if the line is visible company-wide
 */
export function isCompanyWideCampaign (campaign) {
  return [CALL_ROUTER_BEHAVIOR_MODE_ALOAI]
    .includes(campaign?.call_router_behavior) ||
    Boolean(campaign?.is_fax)
}

/**
 * Checks whether an IVR or Dead End line is scoped to (falls back to) a given Inbox.
 *
 * Unlike regular lines, IVR and Dead End lines aren't tied to an Inbox through a ring group;
 * they surface in the Inbox they fall back to — text_ring_group_id for IVR,
 * dead_end_fallback_inbox_id for Dead End. Used to keep these lines visible only inside their
 * fallback Inbox instead of across every Inbox in the company.
 *
 * @param {Object} campaign - Campaign object
 * @param {Number} inboxId - Inbox (ring group) id to scope against
 * @returns {Boolean} - True when campaign is an IVR/Dead End line whose fallback Inbox is inboxId
 */
export function isIvrOrDeadEndCampaignInInbox (campaign, inboxId) {
  if (!inboxId || !isIvrOrDeadEndCampaign(campaign)) {
    return false
  }

  const fallbackInboxId = campaign.call_router_behavior === CALL_ROUTER_BEHAVIOR_MODE_DEAD_END
    ? campaign.dead_end_fallback_inbox_id
    : campaign.ivr?.text_ring_group_id

  return fallbackInboxId === inboxId
}

/**
 * Resolve the inbox (ring group) id that a line/campaign belongs to. Mirrors the
 * mapping used by the message-composer line selector so the watch-only guard and
 * the cross-inbox warning stay consistent.
 * @param {Object} campaign - Campaign/line object
 * @param {Array} teamInboxCampaigns - Loaded team-inbox campaigns, used for IVR fallback
 * @returns {Number|null} - The inbox id, or null when it cannot be resolved
 */
export function getCampaignInboxId (campaign, teamInboxCampaigns = []) {
  if (!campaign) {
    return null
  }

  if (isIvrOrDeadEndCampaign(campaign)) {
    if (campaign.call_router_behavior === CALL_ROUTER_BEHAVIOR_MODE_DEAD_END) {
      return campaign.dead_end_fallback_inbox_id
    }

    const ivrTextRingGroupId = campaign.ivr?.text_ring_group_id

    if (ivrTextRingGroupId) {
      return ivrTextRingGroupId
    }

    // fallback to team inbox campaigns state
    const teamInboxCampaign = teamInboxCampaigns.find(c => c.id === campaign.id)
    return teamInboxCampaign?.ivr?.text_ring_group_id
  }

  return campaign.ring_group_id
}
